import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/hooks/use-toast';
import {
  BLD_SOURCE_KEYS,
  BLD_SOURCE_LABELS,
  type BldReportsConfig,
  type BldReportsConfigSnapshot,
  type BldSourceKey,
  type BldTeamsPrivateChannel,
  getBldReportsConfig,
  isMailboxAddress,
  isSharePointUrl,
  parseTeamsChannelUrl,
  saveBldReportsConfig,
} from '@/lib/bld-reports-api';
import { API_BASE_URL } from '@/lib/config';

const URL_ERROR_TEXT = 'Phải là link https thuộc *.sharepoint.com';

/** Preset giờ chạy theo Asia/Ho_Chi_Minh (thứ 2 → thứ 7). */
const CRON_PRESETS: readonly { value: string; label: string }[] = [
  { value: '45 9 * * *', label: '09:45 hằng ngày' },
  { value: '0 8 * * *', label: '08:00 hằng ngày' },
  { value: '30 9 * * *', label: '09:30 hằng ngày' },
];

interface BldReportsDraft {
  enabled: boolean;
  scheduleCron: string;
  urls: Record<BldSourceKey, string>;
  teamId: string;
  channelId: string;
  channelUrl: string;
  teamsSendEnabled: boolean;
  showDevWarnings: boolean;
  allowOverwriteExistingDays: boolean;
  mailbox: string;
}

const EMPTY_DRAFT: BldReportsDraft = {
  enabled: false,
  scheduleCron: '45 9 * * *',
  urls: {
    production_inventory: '',
    electrical_structure: '',
    equipment_status: '',
    production_stoppage: '',
  },
  teamId: '',
  channelId: '',
  channelUrl: '',
  teamsSendEnabled: false,
  showDevWarnings: false,
  allowOverwriteExistingDays: false,
  mailbox: '',
};

function buildDraft(config: BldReportsConfig): BldReportsDraft {
  const urls = { ...EMPTY_DRAFT.urls };
  for (const key of BLD_SOURCE_KEYS) urls[key] = config.sharepointUrls[key] ?? '';
  return {
    enabled: config.enabled,
    scheduleCron: config.scheduleCron,
    urls,
    teamId: config.teamsPrivateChannel?.teamId ?? '',
    channelId: config.teamsPrivateChannel?.channelId ?? '',
    channelUrl: '',
    teamsSendEnabled: config.teamsSendEnabled,
    showDevWarnings: config.showDevWarnings,
    allowOverwriteExistingDays: config.allowOverwriteExistingDays,
    mailbox: config.mailbox ?? '',
  };
}

interface BldReportsConfigFormProps {
  className?: string;
  /** Nút "Đóng" hiển thị cạnh nút lưu (form nằm trong Dialog). */
  onClose?: () => void;
  /**
   * API base runtime của S3 Explorer (prop `apiBaseUrl` khi nhúng vào frontend host,
   * vd `https://dev-hoaphat.cagent.cmcts.ai/s3-explorer`). Mặc định `API_BASE_URL`
   * (env `VITE_APP_API_URL`, hoặc host hiện tại + `/s3-explorer` khi build không có env).
   */
  apiBaseUrl?: string;
}

export function BldReportsConfigForm({
  className,
  onClose,
  apiBaseUrl = API_BASE_URL,
}: BldReportsConfigFormProps) {
  const [draft, setDraft] = useState<BldReportsDraft>(EMPTY_DRAFT);
  const [snapshot, setSnapshot] = useState<BldReportsConfigSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getBldReportsConfig(apiBaseUrl)
      .then((data) => {
        if (cancelled) return;
        setSnapshot(data);
        setDraft(buildDraft(data.config));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        toast({
          title: 'Lỗi tải cấu hình báo cáo Ban lãnh đạo',
          description: err instanceof Error ? err.message : String(err),
          variant: 'destructive',
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl]);

  const updateDraft = <K extends keyof BldReportsDraft>(key: K, value: BldReportsDraft[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const invalidUrlKeys = BLD_SOURCE_KEYS.filter((key) => {
    const value = (draft.urls[key] ?? '').trim();
    return value.length > 0 && !isSharePointUrl(value);
  });
  const emptyUrlKeys = BLD_SOURCE_KEYS.filter((key) => (draft.urls[key] ?? '').trim().length === 0);
  const trimmedTeamId = draft.teamId.trim();
  const trimmedChannelId = draft.channelId.trim();
  const teamsChannelPartial = (trimmedTeamId.length > 0) !== (trimmedChannelId.length > 0);
  const cronEmpty = draft.scheduleCron.trim().length === 0;
  const trimmedMailbox = draft.mailbox.trim();
  const mailboxInvalid = trimmedMailbox.length > 0 && !isMailboxAddress(trimmedMailbox);
  const canSave =
    !loading &&
    !saving &&
    invalidUrlKeys.length === 0 &&
    !teamsChannelPartial &&
    !cronEmpty &&
    !mailboxInvalid;

  const handleValidateUrls = () => {
    if (invalidUrlKeys.length > 0) {
      toast({
        title: 'Có link SharePoint không hợp lệ',
        description: invalidUrlKeys.map((key) => BLD_SOURCE_LABELS[key]).join('; '),
        variant: 'destructive',
      });
      return;
    }
    if (emptyUrlKeys.length > 0) {
      toast({
        title: 'Còn nguồn chưa có link SharePoint',
        description: emptyUrlKeys.map((key) => BLD_SOURCE_LABELS[key]).join('; '),
      });
      return;
    }
    toast({ title: 'Tất cả link SharePoint hợp lệ', description: '4/4 nguồn đã cấu hình.' });
  };

  const handleParseChannelUrl = () => {
    const parsed = parseTeamsChannelUrl(draft.channelUrl);
    if (!parsed) {
      toast({
        title: 'Không tách được teamId/channelId',
        description: 'Dán link channel Teams dạng .../l/channel/<channelId>/<name>?groupId=<teamId>.',
        variant: 'destructive',
      });
      return;
    }
    setDraft((prev) => ({ ...prev, teamId: parsed.teamId, channelId: parsed.channelId }));
    toast({
      title: 'Đã tách từ URL',
      description: `teamId=${parsed.teamId} · channelId=${parsed.channelId}`,
    });
  };

  const handleSave = async () => {
    const teamsPrivateChannel: BldTeamsPrivateChannel | null =
      trimmedTeamId && trimmedChannelId
        ? { teamId: trimmedTeamId, channelId: trimmedChannelId }
        : null;
    const sharepointUrls: Partial<Record<BldSourceKey, string>> = {};
    for (const key of BLD_SOURCE_KEYS) sharepointUrls[key] = (draft.urls[key] ?? '').trim();

    setSaving(true);
    try {
      const saved = await saveBldReportsConfig(apiBaseUrl, {
        enabled: draft.enabled,
        schedule_cron: draft.scheduleCron.trim(),
        sharepoint_urls: sharepointUrls,
        teams_private_channel: teamsPrivateChannel,
        teams_send_enabled: draft.teamsSendEnabled,
        show_dev_warnings: draft.showDevWarnings,
        allow_overwrite_existing_days: draft.allowOverwriteExistingDays,
        mailbox: trimmedMailbox.length > 0 ? trimmedMailbox : null,
      });
      setDraft(buildDraft(saved));
      setSnapshot((prev) => (prev ? { ...prev, config: saved, issues: [] } : prev));
      toast({
        title: 'Đã lưu cấu hình báo cáo Ban lãnh đạo',
        description: draft.enabled ? 'Scheduler đang bật.' : 'Scheduler đang tắt.',
      });
      // Cập nhật lại warnings/issues (ví dụ nguồn vừa được cấu hình link).
      getBldReportsConfig(apiBaseUrl)
        .then((fresh) => setSnapshot(fresh))
        .catch(() => undefined);
    } catch (err: unknown) {
      toast({
        title: 'Lỗi lưu cấu hình báo cáo Ban lãnh đạo',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className={`min-h-0 overflow-y-auto py-2 pr-1 ${className ?? ''}`}>
          <p className="text-sm text-muted-foreground">Đang tải cấu hình...</p>
        </div>
        <DialogFooter>
          {onClose && (
            <Button variant="outline" onClick={onClose}>
              Đóng
            </Button>
          )}
          <Button disabled>Lưu cấu hình</Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <>
      <div className={`min-h-0 space-y-4 overflow-y-auto py-2 pr-1 ${className ?? ''}`}>
      {snapshot?.outlook.configured === false && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3">
          <p className="text-sm font-medium text-red-700">
            Thiếu cấu hình server (env): {snapshot.outlook.missingVars.join(', ')}
          </p>
          <p className="text-xs text-red-700">
            Cần bổ sung biến môi trường phía server trước khi scheduler đọc được SharePoint.
          </p>
        </div>
      )}

      {snapshot && snapshot.issues.length > 0 && (
        <div className="space-y-1 rounded-md border border-red-300 bg-red-50 p-3">
          <p className="text-sm font-medium text-red-700">Cấu hình đang có vấn đề</p>
          <ul className="list-disc space-y-1 pl-4 text-xs text-red-700">
            {snapshot.issues.map((issue) => (
              <li key={`${issue.key}:${issue.message}`}>
                <span className="font-mono">{issue.key}</span>: {issue.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {snapshot && snapshot.warnings.length > 0 && (
        <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3">
          <ul className="list-disc space-y-1 pl-4 text-xs text-yellow-800">
            {snapshot.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Switch
          id="bld-enabled"
          checked={draft.enabled}
          onCheckedChange={(checked) => updateDraft('enabled', checked)}
          disabled={saving}
        />
        <Label htmlFor="bld-enabled">Bật/tắt scheduler báo cáo Ban lãnh đạo</Label>
      </div>

      <div className="space-y-2">
        <Label htmlFor="bld-cron" className="text-xs text-muted-foreground">
          Lịch chạy (cron)
        </Label>
        <Select
          value={CRON_PRESETS.some((preset) => preset.value === draft.scheduleCron) ? draft.scheduleCron : ''}
          onValueChange={(value) => updateDraft('scheduleCron', value)}
          disabled={saving}
        >
          <SelectTrigger id="bld-cron-preset">
            <SelectValue placeholder="Chọn giờ chạy có sẵn" />
          </SelectTrigger>
          <SelectContent>
            {CRON_PRESETS.map((preset) => (
              <SelectItem key={preset.value} value={preset.value}>
                {preset.label} ({preset.value})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          id="bld-cron"
          value={draft.scheduleCron}
          onChange={(e) => updateDraft('scheduleCron', e.target.value)}
          placeholder="45 9 * * *"
          disabled={saving}
          className={cronEmpty ? 'border-red-500' : undefined}
        />
        <p className="text-xs text-muted-foreground">
          Giờ chạy theo Asia/Ho_Chi_Minh (thứ 2 → thứ 7).
        </p>
        {cronEmpty && <p className="text-xs text-red-600">Cron không được để trống.</p>}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-muted-foreground">Link SharePoint (4 nguồn)</Label>
          <Button variant="outline" size="sm" onClick={handleValidateUrls} disabled={saving}>
            Kiểm tra
          </Button>
        </div>
        {BLD_SOURCE_KEYS.map((key) => (
          <div key={key} className="space-y-1">
            <Label htmlFor={`bld-url-${key}`} className="text-xs text-muted-foreground">
              {BLD_SOURCE_LABELS[key]}
            </Label>
            <Input
              id={`bld-url-${key}`}
              value={draft.urls[key] ?? ''}
              onChange={(e) => updateDraft('urls', { ...draft.urls, [key]: e.target.value })}
              placeholder="https://<tenant>.sharepoint.com/..."
              disabled={saving}
              className={invalidUrlKeys.includes(key) ? 'border-red-500' : undefined}
            />
            {invalidUrlKeys.includes(key) && (
              <p className="text-xs text-red-600">{URL_ERROR_TEXT}</p>
            )}
          </div>
        ))}
        <p className="text-xs text-muted-foreground">
          Để trống nghĩa là chưa cấu hình nguồn đó; server sẽ bỏ qua nguồn chưa có link.
        </p>
      </div>

      <div className="space-y-3 rounded-md border p-3">
        <Label className="text-xs text-muted-foreground">
          Teams private channel (nhận &quot;sự cố đặc biệt&quot;)
        </Label>
        <div className="space-y-1">
          <Label htmlFor="bld-team-id" className="text-xs text-muted-foreground">
            teamId
          </Label>
          <Input
            id="bld-team-id"
            value={draft.teamId}
            onChange={(e) => updateDraft('teamId', e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            disabled={saving}
            className={teamsChannelPartial ? 'border-red-500' : undefined}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="bld-channel-id" className="text-xs text-muted-foreground">
            channelId
          </Label>
          <Input
            id="bld-channel-id"
            value={draft.channelId}
            onChange={(e) => updateDraft('channelId', e.target.value)}
            placeholder="19:xxxxx@thread.tacv2"
            disabled={saving}
            className={teamsChannelPartial ? 'border-red-500' : undefined}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="bld-channel-url" className="text-xs text-muted-foreground">
            Link channel Teams (để tách tự động)
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="bld-channel-url"
              value={draft.channelUrl}
              onChange={(e) => updateDraft('channelUrl', e.target.value)}
              placeholder="https://teams.microsoft.com/l/channel/...?groupId=..."
              disabled={saving}
            />
            <Button variant="outline" size="sm" onClick={handleParseChannelUrl} disabled={saving}>
              Tách từ URL
            </Button>
          </div>
        </div>
        {teamsChannelPartial && (
          <p className="text-xs text-red-600">
            Cần nhập đủ cả teamId và channelId (hoặc bỏ trống cả hai để dùng URL).
          </p>
        )}
      </div>

      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Switch
            id="bld-teams-send"
            checked={draft.teamsSendEnabled}
            onCheckedChange={(checked) => updateDraft('teamsSendEnabled', checked)}
            disabled={saving}
          />
          <Label htmlFor="bld-teams-send">Gửi báo cáo vào Teams</Label>
        </div>
        <p className="text-xs text-muted-foreground">
          Mặc định tắt. Chỉ gửi khi bật và khi chạy thật.
        </p>
      </div>

      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Switch
            id="bld-show-dev-warnings"
            checked={draft.showDevWarnings}
            onCheckedChange={(checked) => updateDraft('showDevWarnings', checked)}
            disabled={saving}
          />
          <Label htmlFor="bld-show-dev-warnings">Hiện thông tin chẩn đoán (dev)</Label>
        </div>
        <p className="text-xs text-muted-foreground">
          Thêm chip nguồn/ngày dữ liệu và cảnh báo kỹ thuật vào HTML + card Teams. Mặc định tắt.
        </p>
      </div>

      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Switch
            id="bld-allow-overwrite"
            checked={draft.allowOverwriteExistingDays}
            onCheckedChange={(checked) => updateDraft('allowOverwriteExistingDays', checked)}
            disabled={saving}
          />
          <Label htmlFor="bld-allow-overwrite">Cho phép ghi đè dữ liệu ngày đã có</Label>
        </div>
        <p className="text-xs text-muted-foreground">
          Mặc định tắt — ngày đã ghi (đã báo cáo) không bị sửa, chỉ ghi thêm ngày còn thiếu. Bật khi
          cần refresh theo file nguồn hoặc backfill lại dữ liệu đã báo cáo.
        </p>
      </div>

      <div className="space-y-1">
        <Label htmlFor="bld-mailbox" className="text-xs text-muted-foreground">
          Mailbox Ban lãnh đạo (API CEO Assistant/MCP)
        </Label>
        <Input
          id="bld-mailbox"
          type="email"
          value={draft.mailbox}
          onChange={(e) => updateDraft('mailbox', e.target.value)}
          placeholder="leader@hoaphat.com.vn"
          disabled={saving}
          className={mailboxInvalid ? 'border-red-500' : undefined}
        />
        <p className="text-xs text-muted-foreground">
          Dùng cho 2 API app-only unread-messages/calendar khi gọi không kèm ?mailbox=.
        </p>
        {mailboxInvalid && (
          <p className="text-xs text-red-600">Phải là địa chỉ email (vd: leader@hoaphat.com.vn).</p>
        )}
      </div>
      </div>

      <DialogFooter>
        {onClose && (
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Đóng
          </Button>
        )}
        <Button onClick={() => void handleSave()} disabled={!canSave}>
          {saving ? 'Đang lưu...' : 'Lưu cấu hình'}
        </Button>
      </DialogFooter>
    </>
  );
}
